'use client';

import React from 'react';

import { Link } from '@/app/_i18n/routing';
import { TopicsBlockProps } from '../../_blocks/TopicsBlockGraphQL/types';
import type { Media as MediaType, Page } from '../../../payload/payload-types';
import classes from './TopicsBlock.module.scss';
import { DebugJsonBlock } from '../DebugJsonBlock';
import { Media } from '../Media';
import type { ImageSizeValue } from '../Media/types';
import { Gutter } from '../Gutter';
import { SemanticHeading } from '../SemanticHeading';

export const TopicsBlock: React.FC<{
  className?: string;
  topicsBlock: TopicsBlockProps;
}> = ({ className, topicsBlock }) => {
  const [supportsScrollMarkers, setSupportsScrollMarkers] = React.useState<boolean | null>(null);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const itemRefs = React.useRef<(HTMLDivElement | null)[]>([]);

  const gridStyle =
    topicsBlock.topics.length === 2 || topicsBlock.topics.length === 4 ? 'col-6' : 'col-4';
  const imageSize: ImageSizeValue =
    topicsBlock.topics.length === 2 || topicsBlock.topics.length === 4 ? 'widescreen' : 'landscape';

  // Feature detection for scroll markers
  React.useEffect(() => {
    // Check if CSS.supports is available and test for scroll marker support
    const hasScrollMarkerSupport =
      typeof CSS !== 'undefined' && CSS.supports && CSS.supports('scroll-marker-group', 'after');
    setSupportsScrollMarkers(hasScrollMarkerSupport);
  }, []);

  // Track scroll position for manual indicators
  React.useEffect(() => {
    if (supportsScrollMarkers || !scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    let ticking = false;

    const updateActiveIndex = () => {
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const containerCenter = containerRect.left + containerRect.width / 2;

      let closestIndex = 0;
      let closestDistance = Infinity;

      itemRefs.current.forEach((item, index) => {
        if (!item) return;

        const itemRect = item.getBoundingClientRect();
        const itemCenter = itemRect.left + itemRect.width / 2;
        const distance = Math.abs(itemCenter - containerCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      setActiveIndex(closestIndex);
      ticking = false;
    };

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(updateActiveIndex);
        ticking = true;
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });

    // Initial update
    updateActiveIndex();

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [supportsScrollMarkers]);

  // Handle manual dot clicks
  const handleDotClick = (index: number) => {
    if (!scrollContainerRef.current || !itemRefs.current[index]) return;

    const container = scrollContainerRef.current;
    const item = itemRefs.current[index];

    if (item) {
      const containerRect = container.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      const scrollLeft = container.scrollLeft + itemRect.left - containerRect.left;

      container.scrollTo({
        left: scrollLeft,
        behavior: 'smooth',
      });
    }
  };

  return (
    <Gutter>
      <SemanticHeading
        heading={topicsBlock.title}
        fallbackText={topicsBlock.title_legacy}
        fallbackTag="h2"
        className={`${classes.title} heading-h2`}
      />
      <div
        className={[className, classes.wrapper, 'grid'].filter(Boolean).join(' ')}
        ref={scrollContainerRef}
      >
        {topicsBlock.topics.map((topic, index) =>
          topic.hideTopic ? null : (
            <div
              className={`${gridStyle} ${classes.item}`}
              key={topic.id}
              ref={el => {
                itemRefs.current[index] = el;
              }}
            >
              {topic.linkType === 'custom' ? (
                <Link
                  href={topic.url || '/'}
                  target={topic.newTab ? '_blank' : undefined}
                  rel={topic.newTab ? 'noopener noreferrer' : undefined}
                >
                  <div className={classes.itemCardImage}>
                    <Media resource={topic.image} className={classes.image} imageSize={imageSize} />
                    <SemanticHeading
                      heading={topic.label}
                      fallbackText={topic.label_legacy}
                      fallbackTag="span"
                      className={classes.itemCardLabel}
                    />
                  </div>
                </Link>
              ) : (
                <Link
                    href={topic.link && typeof topic.link === 'object'
                      ? `${(topic.link as Page).parentSlug ? (topic.link as Page).parentSlug + '/' : ''}${(topic.link as Page).slug}/`.replace('//', '/')
                      : '/'}
                  target={topic.newTab ? '_blank' : undefined}
                  rel={topic.newTab ? 'noopener noreferrer' : undefined}
                >
                  <div className={classes.itemCardImage}>
                    <Media resource={topic.image} className={classes.image} imageSize={imageSize} />
                    <SemanticHeading
                      heading={topic.label}
                      fallbackText={topic.label_legacy}
                      fallbackTag="span"
                      className={classes.itemCardLabel}
                    />
                  </div>
                </Link>
              )}
            </div>
          ),
        )}
        <DebugJsonBlock block={topicsBlock} />
      </div>

      {/* Manual scroll indicators for browsers without scroll marker support */}
      {supportsScrollMarkers === false && (
        <div className={classes.manualScrollMarkers}>
          {topicsBlock.topics.map((_, index) => (
            <button
              key={index}
              className={`${classes.manualScrollMarker} ${
                index === activeIndex ? classes.active : ''
              }`}
              onClick={() => handleDotClick(index)}
              aria-label={`Go to item ${index + 1}`}
            />
          ))}
        </div>
      )}
    </Gutter>
  );
};

export default TopicsBlock;
